/**
 * Build WINGO Wrapped stats for a user from Ledger and Log data
 */

import type {
  LedgerRow,
  LogRow,
  WingoWrappedStats,
} from './wrappedTypes';
import { wrappedCopy } from './wrappedCopy';

/**
 * Parse date string in mm/dd/yy format to Date object
 * Handles both single and double-digit months/days
 */
function parseDate(dateStr: string): Date | null {
  if (!dateStr || !dateStr.trim()) return null;

  try {
    // Handle formats like "5/28/25", "05/28/25", "8-23-25", "8/23/25"
    const normalized = dateStr.trim().replace(/-/g, '/');
    const parts = normalized.split('/');
    
    if (parts.length !== 3) return null;

    const month = parseInt(parts[0], 10) - 1; // JS months are 0-indexed
    const day = parseInt(parts[1], 10);
    let year = parseInt(parts[2], 10);

    // Handle 2-digit years (assume 2000s)
    if (year < 100) {
      year += 2000;
    }

    if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
    if (month < 0 || month > 11) return null;
    if (day < 1 || day > 31) return null;

    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;

    return date;
  } catch {
    return null;
  }
}

/**
 * Calculate median of an array of numbers
 */
function median(numbers: number[]): number | null {
  if (numbers.length === 0) return null;
  
  const sorted = [...numbers].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

/**
 * Group mining sessions by date and activity link
 */
interface MiningSession {
  date: Date;
  wingo: number;
  sourceType?: string | null;
  activityLink?: string | null;
}

function groupMiningSessions(logRows: LogRow[]): MiningSession[] {
  const sessions = new Map<string, MiningSession>();

  for (const row of logRows) {
    if (row.Category !== 'Mining' || row['WINGO +/-'] <= 0) continue;

    const date = parseDate(row.Date);
    if (!date) continue;

    // Create session key: date + activity link (if present)
    const dateKey = date.toISOString().split('T')[0];
    const sessionKey = row['Activity Link']
      ? `${dateKey}:${row['Activity Link']}`
      : dateKey;

    if (sessions.has(sessionKey)) {
      const session = sessions.get(sessionKey)!;
      session.wingo += row['WINGO +/-'];
      // Use first non-null source type
      if (!session.sourceType && row['Source Type']) {
        session.sourceType = row['Source Type'];
      }
    } else {
      sessions.set(sessionKey, {
        date,
        wingo: row['WINGO +/-'],
        sourceType: row['Source Type'] || null,
        activityLink: row['Activity Link'] || null,
      });
    }
  }

  return Array.from(sessions.values());
}

/**
 * Calculate DAISY Trust Score (0-100)
 */
function calculateDaisyTrustScore(
  hasInitiation: boolean,
  totalSessions: number,
  avgWingoPerSession: number | null,
  tenPlusOneCompleted: boolean,
  totalWagers: number,
  totalMined: number
): { score: number; label: string } {
  let score = 0;

  // +20 if InitiationDate exists
  if (hasInitiation) score += 20;

  // +20 if totalSessions >= 5
  if (totalSessions >= 5) score += 20;

  // +20 if avgWingoPerSession is between 3 and 12
  if (avgWingoPerSession !== null && avgWingoPerSession >= 3 && avgWingoPerSession <= 12) {
    score += 20;
  }

  // +15 if tenPlusOneCompleted
  if (tenPlusOneCompleted) score += 15;

  // +10 if totalWagers <= 5 (not too degenerate)
  if (totalWagers <= 5) score += 10;

  // -10 if TotalMined >= 80 AND totalWagers >= 5 (probable whale)
  if (totalMined >= 80 && totalWagers >= 5) {
    score -= 10;
  }

  // Clamp to [0, 100]
  score = Math.max(0, Math.min(100, score));

  // Determine label
  let label: string;
  if (score >= 80) {
    label = wrappedCopy.daisyTrustLabels.trustedMare;
  } else if (score >= 60) {
    label = wrappedCopy.daisyTrustLabels.mostlyReliable;
  } else if (score >= 40) {
    label = wrappedCopy.daisyTrustLabels.chaoticNeutral;
  } else {
    label = wrappedCopy.daisyTrustLabels.statisticalProblem;
  }

  return { score, label };
}

/**
 * Build wrapped stats for a user
 */
/**
 * Calculate OBWI Prize rank based on prize amounts across all users
 * Returns rank (1, 2, 3) or null if not in top 3
 */
function calculateOBWIRank(
  username: string,
  userPrizeAmount: number,
  allLedgerRows: LedgerRow[],
  allLogRows: LogRow[]
): number | null {
  if (userPrizeAmount <= 0) return null;
  
  const normalizedUsername = username.toLowerCase().trim();
  
  // Explicit OBWI rankings: Kirkland (1st), Sam Spade (2nd), Trimbawolf (3rd)
  if (normalizedUsername === 'kirkland' && userPrizeAmount > 0) {
    return 1;
  }
  if (normalizedUsername === 'sam spade' && userPrizeAmount > 0) {
    return 2;
  }
  if (normalizedUsername === 'trimbawolf' && userPrizeAmount > 0) {
    return 3;
  }
  
  // Get all OBWI Prize amounts
  const allPrizeAmounts: Map<string, number> = new Map();
  
  // From ledger
  allLedgerRows.forEach(row => {
    if (row['OBWI Prize'] > 0) {
      const normalized = row.Username.toLowerCase().trim();
      allPrizeAmounts.set(normalized, row['OBWI Prize']);
    }
  });
  
  // From log (sum up for each user)
  allLogRows
    .filter(r => r.Category === 'OBWI Prize' && r['WINGO +/-'] > 0)
    .forEach(row => {
      const normalized = row.Username.toLowerCase().trim();
      const current = allPrizeAmounts.get(normalized) || 0;
      allPrizeAmounts.set(normalized, current + row['WINGO +/-']);
    });
  
  // Get unique prize amounts and sort descending
  const uniqueAmounts = Array.from(new Set(Array.from(allPrizeAmounts.values())))
    .filter(amount => amount > 0)
    .sort((a, b) => b - a);
  
  // Find rank (1-indexed) - rank is position in sorted unique amounts
  const rank = uniqueAmounts.indexOf(userPrizeAmount) + 1;
  
  // Only return rank if in top 3
  return rank >= 1 && rank <= 3 ? rank : null;
}

export function buildWrappedStatsForUser(
  username: string,
  ledgerRows: LedgerRow[],
  logRows: LogRow[]
): WingoWrappedStats {
  // Find user's ledger row
  const ledgerRow = ledgerRows.find(
    (r) => r.Username.toLowerCase().trim() === username.toLowerCase().trim()
  );

  // Use the username from ledger row if available (preserves correct capitalization),
  // otherwise use the provided username
  const displayUsername = ledgerRow ? ledgerRow.Username.trim() : username.trim();

  // Filter log rows for this user
  const userLogRows = logRows.filter(
    (r) => r.Username.toLowerCase().trim() === username.toLowerCase().trim()
  );

  // Initiation Date
  let initiationDate: Date | null = null;
  const initiationRows = userLogRows.filter(
    (r) => r['Initiation?'] === 'Yes'
  );
  
  if (initiationRows.length > 0) {
    const dates = initiationRows
      .map((r) => parseDate(r.Date))
      .filter((d): d is Date => d !== null)
      .sort((a, b) => a.getTime() - b.getTime());
    
    if (dates.length > 0) {
      initiationDate = dates[0];
    }
  } else {
    // Use earliest date across all categories
    const allDates = userLogRows
      .map((r) => parseDate(r.Date))
      .filter((d): d is Date => d !== null)
      .sort((a, b) => a.getTime() - b.getTime());
    
    if (allDates.length > 0) {
      initiationDate = allDates[0];
    }
  }

  // Total WINGO Mined
  let totalWingoMined = 0;
  if (ledgerRow) {
    totalWingoMined = ledgerRow['Total Mined'] || 0;
  } else {
    // Fallback: sum Mining rows with positive WingoDelta
    totalWingoMined = userLogRows
      .filter((r) => r.Category === 'Mining' && r['WINGO +/-'] > 0)
      .reduce((sum, r) => sum + r['WINGO +/-'], 0);
  }

  // Mining sessions
  const miningRows = userLogRows.filter((r) => r.Category === 'Mining');
  const sessions = groupMiningSessions(miningRows);
  
  const sessionWingoValues = sessions.map((s) => s.wingo);
  const totalSessions = sessions.length;
  const avgWingoPerSession =
    sessionWingoValues.length > 0
      ? sessionWingoValues.reduce((a, b) => a + b, 0) / sessionWingoValues.length
      : null;
  const medianWingoPerSession = median(sessionWingoValues);

  // Highest WINGO Session
  const highestSession =
    sessions.length > 0
      ? sessions.reduce((max, s) => (s.wingo > max.wingo ? s : max), sessions[0])
      : null;

  // Wagers
  const wagerRows = userLogRows.filter((r) => r.Category === 'Wager');
  const totalWagers = wagerRows.length;
  const netWagerWingo = wagerRows.reduce((sum, r) => sum + r['WINGO +/-'], 0);

  // Last Wager Date
  let lastWagerDate: Date | null = null;
  if (ledgerRow && ledgerRow.Wager > 0 && ledgerRow['Date of Last Transaction']) {
    lastWagerDate = parseDate(ledgerRow['Date of Last Transaction']);
  }
  
  if (!lastWagerDate && wagerRows.length > 0) {
    const wagerDates = wagerRows
      .map((r) => parseDate(r.Date))
      .filter((d): d is Date => d !== null)
      .sort((a, b) => b.getTime() - a.getTime());
    
    if (wagerDates.length > 0) {
      lastWagerDate = wagerDates[0];
    }
  }

  // Calculate OBWI Prize amount
  let obwiPrizeAmount = 0;
  if (ledgerRow && ledgerRow['OBWI Prize'] > 0) {
    obwiPrizeAmount = ledgerRow['OBWI Prize'];
  } else {
    // Sum up WINGO from log rows with OBWI Prize category
    obwiPrizeAmount = userLogRows
      .filter((r) => r.Category === 'OBWI Prize')
      .reduce((sum, r) => sum + (r['WINGO +/-'] || 0), 0);
  }
  
  // Calculate OBWI rank (only top 3 are winners)
  const obwiRank = calculateOBWIRank(displayUsername, obwiPrizeAmount, ledgerRows, logRows);
  const hasOBWIPrize = obwiRank !== null;
  
  const hasYellowstoneCompletion = ledgerRow ? ledgerRow.Yellowstone > 0 : false;
  
  const hasDancingDaisy =
    (ledgerRow && ledgerRow['Dancing Daisy'] > 0) ||
    userLogRows.some((r) => r.Category === 'Dancing Daisy');
  
  const hasGateUnlock =
    (ledgerRow && ledgerRow['Gate Relock'] > 0) ||
    userLogRows.some((r) => r.Category === 'Gate Unlock');

  // 10+1 (Membership / Gate Unlock)
  const tenPlusOneCompleted =
    hasGateUnlock || (ledgerRow ? ledgerRow['Total Balance'] >= 10 : false);

  // DAISY Trust Score
  const { score: daisyTrustScore, label: daisyTrustLabel } =
    calculateDaisyTrustScore(
      initiationDate !== null,
      totalSessions,
      avgWingoPerSession,
      tenPlusOneCompleted,
      totalWagers,
      totalWingoMined
    );

  // DAISY's DVOT (3-digit random number, deterministic based on username)
  // Use username as seed for deterministic randomness
  const usernameHash = displayUsername.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const dvotScore = 100 + (usernameHash % 900); // Random number between 100-999

  // Get distance and rank from ledger row
  const distanceKM = ledgerRow ? ledgerRow['Distance (KM)'] || undefined : undefined;
  const rank = ledgerRow ? ledgerRow.Rank || undefined : undefined;
  const votingShare = ledgerRow && ledgerRow['Voting Share (%)'] 
    ? parseFloat(ledgerRow['Voting Share (%)']) || undefined 
    : undefined;

  return {
    username: displayUsername,
    initiationDate,
    totalWingoMined,
    highestWingoSession: highestSession
      ? {
          date: highestSession.date,
          wingo: highestSession.wingo,
          sourceType: highestSession.sourceType,
        }
      : null,
    totalSessions,
    avgWingoPerSession,
    medianWingoPerSession,
    totalWagers,
    netWagerWingo,
    lastWagerDate,
    hasOBWIPrize,
    obwiPrizeAmount,
    obwiRank: obwiRank || undefined,
    hasYellowstoneCompletion,
    hasDancingDaisy,
    hasGateUnlock,
    tenPlusOneCompleted,
    daisyTrustScore,
    daisyTrustLabel,
    dvotScore,
    distanceKM,
    rank,
    votingShare,
  };
}

